import { useParams, useNavigate } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CalendarDays, Check, ChevronsUpDown } from 'lucide-react';
import {
	Button,
	Popover,
	PopoverContent,
	PopoverTrigger,
	cn,
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
} from '@hotelise/ui';

import {
	clearPropertyDetails,
	getProperties,
	getPropertiesList,
	getPropertyDetails,
	getPropertyDetailsInfo,
	getPropertyExpenses,
	getPropertyReservations,
	getPropertyStatements,
} from 'state/modules/properties';

import { PropertyInfo } from 'components/molecules';

import {
	PropertyReservationsList,
	PropertyExpensesList,
	PropertyDetailsFilters,
	PropertyStatementsList,
	PropertySettings,
} from 'components/organisms';

const PropertyDetailsPage = (): JSX.Element => {
	const hashTab = (window.location.hash?.replace('#', '') || 'reservations').toLowerCase();
	const [currentTab, setCurrentTab] = useState(hashTab);
	const [switcherOpen, setSwitcherOpen] = useState(false);

	const params = useParams() as any;
	const navigate = useNavigate();

	const propertyDetails = useSelector(getPropertyDetailsInfo);
	const propertiesList = useSelector(getPropertiesList);

	const dispatch = useDispatch();

	const propertyId = useMemo(() => params.id, [params]);

	useEffect(() => {
		const onHashChange = () => {
			const newTab = (window.location.hash?.replace('#', '') || 'reservations').toLowerCase();
			setCurrentTab(newTab);
		};

		window.addEventListener('hashchange', onHashChange);

		return () => {
			window.removeEventListener('hashchange', onHashChange);
		};
	}, []);

	useEffect(() => {
		if (propertyId) {
			dispatch(getPropertyDetails(propertyId));
			dispatch(getPropertyReservations(propertyId));
			dispatch(getPropertyExpenses(propertyId));
			dispatch(
				getPropertyStatements({
					propertyId,
				}),
			);
		}

		return () => {
			dispatch(clearPropertyDetails());
		};
	}, [propertyId, dispatch]);

	useEffect(() => {
		if (propertiesList.length === 0) {
			dispatch(getProperties());
		}
	}, [propertiesList.length, dispatch]);

	const selectedProperty = propertiesList.find((p) => p.id === propertyId);

	const handlePropertyChange = (pid: string) => {
		navigate(`/properties/${pid}${window.location.hash}`);
		setSwitcherOpen(false);
	};

	const handleTabChange = (value: string) => {
		window.location.hash = value;
	};

	return (
		<div className="w-full h-screen relative overflow-y-auto bg-background md:h-auto md:min-h-screen" id="propertyDetailsListContainer">
			<header className="mb-4 md:mb-6 space-y-3">
				<div className="flex flex-wrap items-center gap-2 md:gap-3">
					{/* Property Switcher */}
					<Popover open={switcherOpen} onOpenChange={setSwitcherOpen}>
						<PopoverTrigger asChild>
							<Button
								variant="outline"
								role="combobox"
								aria-expanded={switcherOpen}
								className="w-full sm:w-64 justify-between"
							>
								<span className="truncate text-left">
									{selectedProperty ? selectedProperty.name : 'Select property'}
								</span>
								<ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
							</Button>
						</PopoverTrigger>
						<PopoverContent className="p-0 w-80" align="start">
							<Command>
								<CommandInput placeholder="Search properties..." className="h-8" />
								<CommandList>
									<CommandEmpty>No property found.</CommandEmpty>
									<CommandGroup>
										{propertiesList.map((p) => (
											<CommandItem
												key={p.id}
												value={p.name}
												onSelect={() => handlePropertyChange(p.id)}
											>
												<Check className={cn('mr-2 h-4 w-4', p.id === propertyId ? 'opacity-100' : 'opacity-0')} />
												<span className="truncate">{p.name}</span>
											</CommandItem>
										))}
									</CommandGroup>
								</CommandList>
							</Command>
						</PopoverContent>
					</Popover>

					{/* View Calendar */}
					{propertyDetails && (
						<Button variant="outline" onClick={() => navigate(`/calendar?property=${propertyDetails.id}`)}>
							<CalendarDays className="h-4 w-4 mr-1.5" />
							View Calendar
						</Button>
					)}

					{/* Back to list */}
					<Button variant="outline" onClick={() => navigate('/properties')}>
						Back to list
					</Button>
				</div>
			</header>

			{propertyDetails && (
				<div className="mb-8 md:mb-5 sm:mb-4">
					<PropertyInfo data={propertyDetails} />
				</div>
			)}

			<div className="pt-2 pb-4">
				<Tabs value={currentTab} onValueChange={handleTabChange}>
					<div className="overflow-x-auto">
						<TabsList className="inline-flex w-max">
							<TabsTrigger value="reservations">Reservations</TabsTrigger>
							<TabsTrigger value="expenses">Expenses</TabsTrigger>
							<TabsTrigger value="statements">Statements</TabsTrigger>
							<TabsTrigger value="settings">Settings</TabsTrigger>
						</TabsList>
					</div>

					<TabsContent value="reservations" className="mt-4">
						<div className="flex items-center justify-between gap-3 flex-wrap">
							<PropertyDetailsFilters propertyId={propertyId} />
						</div>
						<div className="mt-3">
							{propertyDetails && <PropertyReservationsList propertyDetails={propertyDetails} />}
						</div>
					</TabsContent>

					<TabsContent value="expenses" className="mt-4">
						<div className="flex items-center justify-between gap-3 flex-wrap">
							<PropertyDetailsFilters propertyId={propertyId} />
						</div>
						<div className="mt-0">
							<PropertyExpensesList propertyId={propertyId} />
						</div>
					</TabsContent>

					<TabsContent value="statements" className="mt-4">
						<PropertyStatementsList propertyId={propertyId} />
					</TabsContent>

					<TabsContent value="settings" className="mt-4">
						<PropertySettings />
					</TabsContent>
				</Tabs>
			</div>
		</div>
	);
};

export default PropertyDetailsPage;
